/**
 * SyncToTenantButton Component
 *
 * Main button for triggering tenant sync from Grab portal.
 * Shows preview modal when clicked.
 */
import React, { useState } from 'react';
import { Button, notification, Spin, Tooltip } from '@duxton-web/ui';

import { TenantSyncConfig } from '../config';
import { TOOLTIP_MESSAGES } from '../constants/messages';
import { useSyncPreview, useTenantSyncEnabled } from '../hooks';
import { SyncPreviewModal } from './SyncPreviewModal';

interface SyncToTenantButtonProps {
  config: TenantSyncConfig;
  variableName: string;
  /** Disable button when user has unsaved rollout changes */
  disabled?: boolean;
}

export const SyncToTenantButton = ({
  config,
  variableName,
  disabled = false,
}: SyncToTenantButtonProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const isFeatureEnabled = useTenantSyncEnabled(config);

  const {
    data: previewData,
    isFetching: isPreviewLoading,
    refetch: refetchPreview,
  } = useSyncPreview(variableName, config, {
    enabled: false,
  });

  // Don't render if feature flag is disabled
  if (!isFeatureEnabled) {
    return null;
  }

  const handleClick = async () => {
    // Fetch preview data on demand and only open modal on success
    const result = await refetchPreview();
    if (result.isSuccess) {
      setModalOpen(true);
    } else if (result.isError) {
      const errorMessage =
        (result.error as any)?.response?.data?.arg ||
        result.error?.message ||
        'Unknown error';
      notification.error({
        message: 'Failed to load sync preview',
        description: errorMessage,
        placement: 'bottomLeft',
      });
    }
  };

  const isDisabled = disabled || isPreviewLoading;
  const tooltipTitle = disabled
    ? TOOLTIP_MESSAGES.syncButtonDisabled
    : TOOLTIP_MESSAGES.syncButtonEnabled;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Tooltip title={tooltipTitle}>
        <Button
          size="small"
          type="primary"
          disabled={isDisabled}
          onClick={handleClick}
        >
          {isPreviewLoading ? (
            <Spin size="small" />
          ) : (
            `Sync Rollouts to ${config.name}`
          )}
        </Button>
      </Tooltip>

      {previewData && (
        <SyncPreviewModal
          open={modalOpen}
          onCancel={() => setModalOpen(false)}
          variableName={variableName}
          previewData={previewData}
          config={config}
        />
      )}
    </div>
  );
};
